import React, { useEffect, useRef } from 'react';
import { GenericVideoPlayerInterface } from '../../videoUtils';

/**
 * RoundProgressBarRefDriven
 *
 * This component is intentionally *not* updated through React props or state.
 * Instead, it exposes refs (circleRef + textRef) that the parent updates
 * directly inside a requestAnimationFrame loop.
 *
 * Why?
 * - Avoids React re-renders at ~60fps (bad for performance, especially on mobile)
 * - Keeps React completely asleep during video playback
 * - Allows smooth, cheap DOM updates driven by the YouTube player
 *
 * TL;DR — Visual only. Logic lives in the parent. All animation happens via refs.
 */
function RoundProgressBarRefDriven({
    circleRef,
    textRef,
    radius,
    circumference,
    className,
}: {
    circleRef: React.RefObject<SVGCircleElement>;
    textRef: React.RefObject<HTMLDivElement>;
    radius: number;
    circumference: number;
    className?: string;
}) {
    return (
        <div className={className}>
            <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="60" cy="60" r={radius} stroke="#444" strokeWidth="8" fill="none" />
                <circle
                    ref={circleRef}
                    cx="60"
                    cy="60"
                    r={radius}
                    stroke="#ffffffff"
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference}
                />
            </svg>
            <div
                ref={textRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                0%
            </div>
        </div>
    );
}

/**
 * VideoRoundProgressBar
 *
 * Container component that runs the requestAnimationFrame loop.
 * It reads time/duration from any video player (via playerRef)
 * and updates the visual progress bar through refs only.
 *
 * No React state is used — React does not re-render during playback.
 *
 */
export function VideoRoundProgressBar({
    playerRef,
    force100,
    className,
}: {
    playerRef: React.MutableRefObject<GenericVideoPlayerInterface | null>;
    force100?: boolean; // Force percent to be 100
    className?: string;
}) {
    const rafRef = useRef<number | null>(null); // Request animation frame ref
    const circleRef = useRef<SVGCircleElement>(null);
    const textRef = useRef<HTMLDivElement>(null);

    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const lastPercent = useRef(0);

    const setVisualPercent = (percent: number) => {
        const clamped = Math.max(0, Math.min(100, percent));
        const offset = circumference * (1 - clamped / 100);

        // Always update the circle (smooth animation)
        if (circleRef.current) {
            circleRef.current.style.strokeDashoffset = String(offset);
        }

        // Only update text when it actually changes
        if (clamped !== lastPercent.current) {
            lastPercent.current = clamped;
            if (textRef.current) {
                textRef.current.textContent = clamped + '%';
            }
        }
    };

    useEffect(() => {
        // If forced to 100%, skip ticking entirely
        if (force100) {
            setVisualPercent(100);
            return; // Do not start RAF loop
        }

        const tick = () => {
            const player = playerRef.current;

            if (!player) {
                rafRef.current = requestAnimationFrame(tick);
                return;
            }

            const videoTime = player.getCurrentTime();
            const videoDuration = player.getDuration();

            if (!videoDuration) {
                rafRef.current = requestAnimationFrame(tick);
                return;
            }

            const percent = Math.floor((videoTime / videoDuration) * 100);
            setVisualPercent(percent);

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current!);
    }, [force100]);

    return (
        <RoundProgressBarRefDriven
            className={className}
            circleRef={circleRef}
            textRef={textRef}
            radius={radius}
            circumference={circumference}
        />
    );
}
